import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import { Button, Icon, Spinner } from '@agconnections/grow-ui';
import useAppliedProducts from 'hooks/useAppliedProducts';
import ConfirmationModal from 'screens/Integrations/components/ConfirmationModal';
import { MccainContext } from 'screens/Integrations/MccainIntegration/context/Provider';
import { SubmissionSummaryContext } from 'screens/Integrations/SubmissionSummary/context/SubmissionSummaryProvider';
import { getCropZonesFromProperties } from 'helpers/propertyHelpers';
import { MCCAIN_PDF_WRAPPER } from 'screens/Integrations/MccainIntegration/constants';
import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import StatusMessage from 'screens/Integrations/SubmissionSummary/components/TopHeader/components/StatusMessage';
import { paths } from 'routes/paths';
import TopHeader from './components/TopHeader';
import MccainSummaryFarms from './components/MccainSummaryFarms';
import MccainFooterIntegration from '../MccainFooterIntegration';
import FieldsView from './components/FieldsView';

export { MCCAIN_PDF_WRAPPER };

// Builds a map of cropzoneId -> products[] from the persisted
// mcCainExtraData payload (cwfData.extraData.mcCainExtraData). The products
// stored there already match the shape FieldProductsTable consumes during
// the creation flow (productName, type, appliedDate, appliedDateEpoch,
// applicationMethod, rate, total, manufacturerName), so they can be plugged
// straight into the summary view without further adaptation.
const buildProductsByCropzoneIdFromExtraData = mcCainExtraData => {
  const map = {};
  (mcCainExtraData?.farm ?? []).forEach(farm => {
    (farm.field ?? []).forEach(field => {
      (field.cropZone ?? []).forEach(cropZone => {
        if (!cropZone?.id) return;
        map[cropZone.id] = cropZone.products ?? [];
      });
    });
  });
  return map;
};

// Maps cwfData farms (farms[].fields[].cropzones[]) into the structure the
// McCain summary view consumes (farms[].fields[].cropzones[].products[]),
// sourcing products from mcCainExtraData by cropzone id so the view shows
// exactly the same products that were submitted to McCain.
const adaptCwfFarmsToSelectedProperties = (farms, productsByCropzoneId) =>
  (farms ?? [])
    .map(farm => ({
      ...farm,
      fields: (farm.fields ?? [])
        .map(field => ({
          ...field,
          cropzones: (field.cropzones ?? [])
            .map(cz => ({
              ...cz,
              products: productsByCropzoneId[cz.id] ?? []
            }))
            .filter(cz => cz.products.length)
        }))
        .filter(field => field.cropzones.length)
    }))
    .filter(farm => farm.fields.length);

const MccainSummaryView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { values, setFieldValue } = useFormikContext();
  const { selectedProperties, selectedCropSeasons, selectedFarm } = values;
  const { getAppliedProducts } = useAppliedProducts();

  const {
    setModalSubmissionState,
    state: { openSubmissionModal }
  } = useContext(MccainContext);

  const {
    state: {
      isReadOnly,
      farms: cwfFarms,
      extraData: cwfExtraData,
      submissionResult
    }
  } = useContext(SubmissionSummaryContext);

  const history = useHistory();
  const handleBackToLanding = () =>
    history.push(`${paths.integrations}/${INTEGRATIONS.mccain}/landing-page`);

  const mccainSubmissionResult = useMemo(() => {
    if (!submissionResult) return submissionResult;
    const detailErrors = (submissionResult?.response?.details ?? [])
      .map(detail => detail?.errorMessage)
      .filter(Boolean);
    if (!detailErrors.length) return submissionResult;
    const concatenatedErrors = detailErrors.join('\n');
    return {
      ...submissionResult,
      errorMessage: `The submission to McCain failed with the following errors:\n${concatenatedErrors}`,
      response: undefined
    };
  }, [submissionResult]);

  const getProductsByCropZoneId = (products, id) =>
    products?.filter?.(product => id === product.cropZoneId) ?? [];

  const adaptFieldsToPayload = (fields, products) =>
    fields
      .map(field => ({
        ...field,
        cropzones: field.cropzones
          .map(cz => ({
            ...cz,
            products: getProductsByCropZoneId(products, cz.id)
          }))
          .filter(cz => cz.products.length)
      }))
      .filter(field => field.cropzones.length);

  useEffect(() => {
    // Read-only / view-from-landing flow: the farm/field/cropzone structure
    // comes from cwfData.data; products come from cwfData.extraData
    // .mcCainExtraData (same shape the original review screen rendered when
    // the submission was created).
    if (isReadOnly) {
      if (cwfFarms?.length) {
        const productsByCropzoneId = buildProductsByCropzoneIdFromExtraData(
          cwfExtraData?.mcCainExtraData
        );
        setFieldValue(
          'selectedProperties',
          adaptCwfFarmsToSelectedProperties(cwfFarms, productsByCropzoneId)
        );
        setIsLoading(false);
      }
      return;
    }

    const selectedCropSeasonsIds = selectedCropSeasons.map(
      cropseason => cropseason.id
    );
    const cropzones = getCropZonesFromProperties(selectedProperties).flatMap(
      cz => ({
        cropZoneId: cz.id,
        seasonsIds: cz.cropSeasons?.map(season => season.id)
      })
    );

    const seasonsCropZonesPayload = selectedCropSeasonsIds
      ?.reduce((acc, seasonId) => {
        acc.push({
          seasonId,
          cropZoneIds: cropzones
            ?.filter(cz => cz.seasonsIds.includes(seasonId))
            ?.map(({ cropZoneId }) => cropZoneId)
        });
        return acc;
      }, [])
      ?.filter(payload => payload.cropZoneIds?.length);

    getAppliedProducts(seasonsCropZonesPayload).then(data => {
      const updatedProperties = [...selectedProperties];

      const toPayload = updatedProperties.map(property => {
        return {
          ...property,
          fields: adaptFieldsToPayload(property.fields, data.products)
        };
      });
      setFieldValue('selectedProperties', toPayload);
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCropSeasons, isReadOnly, cwfFarms, cwfExtraData]);
  return (
    <>
      <div
        className="w-full h-full bg-white flex flex-col px-6 py-4 rounded-xl font-body"
        data-testid="summary-view"
        id={MCCAIN_PDF_WRAPPER}
      >
        {isReadOnly && (
          <div className="border-b-1 mb-4" data-html2canvas-ignore>
            <div className="w-full flex h-10 mb-4 items-center font-semibold">
              <Button
                data-testid="back-button-header"
                circular
                style={{
                  borderRadius: 999,
                  marginRight: '0.5rem',
                  height: '2.5rem'
                }}
                id="return-history-button"
                type="outline"
                onClick={handleBackToLanding}
              >
                <Icon icon="chevron-left" color="#718096" />
              </Button>
              <p className="text-2xl ml-4">View Submission</p>
            </div>
            <StatusMessage submissionResult={mccainSubmissionResult} />
          </div>
        )}
        <div
          className="flex flex-col w-full h-48 border-b-2 mb-12"
          id="mccain-top-header"
        >
          <TopHeader />
        </div>
        {isLoading ? (
          <div className="m-auto">
            <Spinner />
          </div>
        ) : (
          <>
            <div
              className="flex flex-row w-full h-full"
              id="mccain-summaryview"
            >
              <div className="w-auto mr-12" data-html2canvas-ignore>
                <MccainSummaryFarms />
              </div>
              <div className="w-full">
                <FieldsView fields={selectedProperties[selectedFarm]?.fields} />
              </div>
            </div>
            {!isReadOnly && (
              <MccainFooterIntegration
                MCCAIN_PDF_WRAPPER={MCCAIN_PDF_WRAPPER}
              />
            )}
          </>
        )}
      </div>
      <ConfirmationModal
        open={openSubmissionModal}
        setOpenModal={setModalSubmissionState}
      />
    </>
  );
};

export default MccainSummaryView;
